import { getUserSaveError } from "../utils/userManagementErrors";
// components/UserManagement.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  useTheme,
  InputAdornment,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  Tooltip,
  Fab,
  Slide,
  Fade,
} from "@mui/material";
import {
  KeyboardArrowDown as DropdownArrowIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  VpnKey as KeyIcon,
  Dns as ResourceIcon,
  FlashOn as ActionIcon,
  Radar as ScopeIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  Assignment as AssignmentIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import "./UserManagement.css";
import UserManagementIcon from "./UserManagementIcon";
import ConfirmationDialog from "./ConfirmationDialog";
import UserFormDialog from "./UserFormDialog";
import RoleFormDialog from "./RoleFormDialog";
import ResourceFormDialog from "./ResourceFormDialog";
import ActionFormDialog from "./ActionFormDialog";
import ScopeFormDialog from "./ScopeFormDialog";
import rbacApi from "../services/rbacApi";
import { useRBAC } from "../context/RBACContext";
import { RESOURCE_DISPLAY_NAMES, ACTION_NAMES } from "../utils/resourceMapping";

// Memoized Permission Form Component
const PermissionFormDialog = React.memo(
  ({
    open,
    onClose,
    onSave,
    mode,
    roles,
    resources,
    actions,
    scopes,
    initialPermission,
    theme,
  }) => {
    const [saveError, setSaveError] = useState("");
    const [saving, setSaving] = useState(false);
    const saveErrorRef = useRef(null);
    useEffect(() => { if (open) setSaveError(""); }, [open, mode, initialPermission]);
    useEffect(() => { if (saveError) saveErrorRef.current?.focus(); }, [saveError]);
    const [permission, setPermission] = useState({
      role_id: "",
      resource_id: "",
      action_id: "",
      scope_id: "",
    });

    useEffect(() => {
      if (open) {
        setPermission(
          initialPermission || {
            role_id: "",
            resource_id: "",
            action_id: "",
            scope_id: "",
          },
        );
      }
    }, [open, initialPermission]);

    const handleSave = async () => {
      if (saving) return;
      setSaveError(""); setSaving(true);
      try { await onSave(permission); }
      catch (error) { setSaveError(getUserSaveError(error)); }
      finally { setSaving(false); }
    };

    const handleClose = () => {
      if (saving) return;
      setSaveError("");
      setPermission({
        role_id: "",
        resource_id: "",
        action_id: "",
        scope_id: "",
      });
      onClose();
    };

    return (
      <Dialog
        className={`user-management-dialog ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            pb: 1,
          }}
        >
          <UserManagementIcon name="permission" className="user-management-title-icon" />
          {mode === "create" ? "Grant Permission" : "Update Permission"}
          <IconButton
            onClick={handleClose}
            aria-label="Close dialog"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {saveError && <Alert severity="error" role="alert" tabIndex={-1} ref={saveErrorRef} sx={{mb:2}}>{saveError}</Alert>}
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                    value={permission.role_id}
                    label="Role"
                    onChange={(e) =>
                      setPermission({ ...permission, role_id: e.target.value })
                    }
                  >
                    {roles.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        <UserManagementIcon name="admin" /> {r.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Resource</InputLabel>
                  <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                    value={permission.resource_id}
                    label="Resource"
                    onChange={(e) =>
                      setPermission({
                        ...permission,
                        resource_id: e.target.value,
                      })
                    }
                  >
                    {resources.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.displayName || r.resourceKey} ({r.resourceType})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Action</InputLabel>
                  <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                    value={permission.action_id}
                    label="Action"
                    onChange={(e) =>
                      setPermission({
                        ...permission,
                        action_id: e.target.value,
                      })
                    }
                  >
                    {actions.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Scope</InputLabel>
                  <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                    value={permission.scope_id}
                    label="Scope"
                    onChange={(e) =>
                      setPermission({ ...permission, scope_id: e.target.value })
                    }
                  >
                    {scopes.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Button onClick={handleClose} color="inherit" disabled={saving}>
            <UserManagementIcon name="cancel" /> Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={handleSave}
            variant="contained"
            sx={{
              bgcolor: theme.palette.primary.main,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          >
            <UserManagementIcon name="tick" /> {mode === "create" ? "Grant" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  // Per-tab search terms (keeps existing UI but scoped per tab)
  const [searchTerms, setSearchTerms] = useState({
    users: "",
    roles: "",
    permissions: "",
    resources: "",
    actions: "",
    scopes: "",
  });
  const tabKeys = ["users", "roles", "permissions", "resources", "actions", "scopes"];
  const { hasResourcePermissionByName } = useRBAC();
  const tableRef = React.useRef(null);

  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Check if user has permission to manage users
  const canManageUsers = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.USER_MANAGEMENT,
    ACTION_NAMES.GET_DATA,
  );

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [actions, setActions] = useState([]);
  const [resources, setResources] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Permission filters (Permissions tab)
  const [permRoleFilter, setPermRoleFilter] = useState(null);
  const [permResourceFilter, setPermResourceFilter] = useState(null);
  const [permActionFilter, setPermActionFilter] = useState(null);
  const [permScopeFilter, setPermScopeFilter] = useState(null);

  // Dialog states
  const [userDialog, setUserDialog] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [roleDialog, setRoleDialog] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [actionDialog, setActionDialog] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [resourceDialog, setResourceDialog] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [scopeDialog, setScopeDialog] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [permissionDialog, setPermissionDialog] = useState({
    open: false,
    mode: "create",
    data: null,
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    type: "user",
    id: null,
    name: "",
    permissionData: null,
  });
  const [initialPermissionData, setInitialPermissionData] = useState({
    role_id: "",
    resource_id: "",
    action_id: "",
    scope_id: "",
  });

  const [updatingUserId, setUpdatingUserId] = useState(null);

  const theme = useTheme();

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          usersData,
          rolesData,
          actionsData,
          resourcesData,
          scopesData,
          permissionsData,
          employeesData,
        ] = await Promise.all([
          rbacApi.get("/user_crud/users").then((res) => res.data),
          rbacApi
            .get("/user_roles/roles")
            .then((res) => res.data)
            .catch(() => []),
          rbacApi
            .get("/user_actions/actions")
            .then((res) => res.data)
            .catch(() => []),
          rbacApi
            .get("/user_resources/resources")
            .then((res) => res.data)
            .catch(() => []),
          rbacApi
            .get("/user_scopes/scopes")
            .then((res) => res.data)
            .catch(() => []),
          rbacApi
            .get("/role_permissions/get_role")
            .then((res) => res.data)
            .catch(() => []),
        ]);

        setUsers(usersData);
        setRoles(rolesData);
        setActions(actionsData);
        setResources(resourcesData);
        setScopes(scopesData);
        setPermissions(permissionsData);
        setEmployees(employeesData);
        // Compute managers list: all users except Super Admin (role id 1)
        const managersList = usersData.filter(u => String(u.roleId) !== '1').map(u => ({ ...u, fullName: `${u.firstName} ${u.lastName}` }));
        setManagers(managersList);
        setError(null);
      } catch (err) {
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // CRUD Handlers
  const handleCreate = async (
    apiPath,
    payload,
    setter,
    list,
    dialogSetter,
    formSetter,
    defaultForm,
  ) => {
    try {
      const response = await rbacApi.post(apiPath, payload);
      setter([...list, response.data]);
      dialogSetter({ open: false, mode: "create", data: null });
      formSetter(defaultForm);
    } catch (err) {
      console.error(err);
      setError(`Failed to create item`);
    }
  };

  //Create User
  const handleCreateUser = useCallback(
    async (formData) => {
      try {

        if (userDialog.mode === "create") {
          const payload = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            contactNumber: formData.contactNumber,
            password: formData.password,
            isActive: formData.isActive ?? true,
            ...(formData.roleId && { roleId: formData.roleId }),
            ...(formData.manager_id && { manager_id: formData.manager_id }),
            ...(formData.scope_id && { scope_id: formData.scope_id }),
          };
          const response = await rbacApi.post("/user_crud/users", payload);
          setUsers([...users, response.data]);
        } else {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        isActive: formData.isActive,
        ...(formData.password && { password: formData.password }),
        ...(formData.roleId && { roleId: formData.roleId }),
        manager_id: formData.manager_id || null,
        scope_id: formData.scope_id ? parseInt(formData.scope_id) : null,
      };
          const response = await rbacApi.put(
            `/user_crud/users/${userDialog.data.userId}`,
            payload,
          );
          setUsers(
            users.map((u) =>
              u.userId === userDialog.data.userId ? response.data : u,
            ),
          );
        }

        setUserDialog({ open: false, mode: "create", data: null });
        setError(null);
      } catch (err) {
        throw new Error(getUserSaveError(err));
      }
    },
    [userDialog, users],
  );

  // Update User Role
  const handleUpdateUserRole = useCallback(
    async (userId, roleId) => {
      try {
        setUpdatingUserId(userId);
        const payload = { roleId: roleId || null };
        const response = await rbacApi.put(
          `/user_crud/users/${userId}`,
          payload,
        );
        setUsers(users.map((u) => (u.userId === userId ? response.data : u)));
        setError(null);
      } catch (err) {
        setError(`Failed to update user role: ${err.message}`);
      } finally {
        setUpdatingUserId(null);
      }
    },
    [users],
  );

  const handleUpdateUserStatus = async (userId, isActive) => {
    try {
      setUpdatingUserId(userId);
      const response = await rbacApi.put(`/user_crud/users/${userId}`, { isActive });
      setUsers(previous => previous.map(user => user.userId === userId ? response.data : user));
      setError(null);
    } catch (err) {
      setError(`Failed to update user status: ${err.message}`);
    } finally { setUpdatingUserId(null); }
  };

  // Update User Manager
  const handleUpdateUserManager = useCallback(
    async (userId, managerId) => {
      try {
        setUpdatingUserId(userId);
        const payload = { manager_id: managerId || null };
        const response = await rbacApi.put(
          `/user_crud/users/${userId}`,
          payload,
        );
        setUsers(users.map((u) => (u.userId === userId ? response.data : u)));
        setError(null);
      } catch (err) {
        setError(`Failed to update user manager: ${err.message}`);
      } finally {
        setUpdatingUserId(null);
      }
    },
    [users],
  );

  // CreateResource

  const handleCreateResource = useCallback(
    async (formData) => {
      try {
        // ✅ Send camelCase fields that match your API
        const payload = {
          resourceKey: formData.resourceKey,
          resourceType: formData.resourceType,
          displayName: formData.displayName,
          description: formData.description,
        };

        if (resourceDialog.mode === "create") {
          const response = await rbacApi.post(
            "/user_resources/resources",
            payload,
          );
          setResources([...resources, response.data]);
        } else {
          const response = await rbacApi.put(
            `/user_resources/resources/${resourceDialog.data.id}`,
            payload,
          );
          setResources(
            resources.map((r) =>
              r.id === resourceDialog.data.id ? response.data : r,
            ),
          );
        }

        setResourceDialog({ open: false, mode: "create", data: null });
        setError(null);
      } catch (err) {
        throw new Error(getUserSaveError(err));
      }
    },
    [resourceDialog, resources],
  );

  // Create Action

  const handleCreateAction = useCallback(
    async (formData) => {
      try {
        const payload = {
          name: formData.name,
          description: formData.description,
          isSystemAction: formData.isSystemAction,
        };

        if (actionDialog.mode === "create") {
          const response = await rbacApi.post("/user_actions/actions", payload);
          setActions([...actions, response.data]);
        } else {
          const response = await rbacApi.put(
            `/user_actions/actions/${actionDialog.data.id}`,
            payload,
          );
          setActions(
            actions.map((a) =>
              a.id === actionDialog.data.id ? response.data : a,
            ),
          );
        }

        setActionDialog({ open: false, mode: "create", data: null });
        setError(null);
      } catch (err) {
        throw new Error(getUserSaveError(err));
      }
    },
    [actionDialog, actions],
  );

  // Create Scope
  const handleCreateScope = useCallback(
    async (formData) => {
      try {
        const payload = {
          name: formData.name,
          description: formData.description || "",
        };

        if (scopeDialog.mode === "create") {
          const response = await rbacApi.post("/user_scopes/scopes", payload);
          setScopes([...scopes, response.data]);
        } else {
          const response = await rbacApi.put(
            `/user_scopes/scopes/${scopeDialog.data.id}`,
            payload,
          );
          setScopes(
            scopes.map((s) =>
              s.id === scopeDialog.data.id ? response.data : s,
            ),
          );
        }

        setScopeDialog({ open: false, mode: "create", data: null });
        setError(null);
      } catch (err) {
        throw new Error(getUserSaveError(err));
      }
    },
    [scopeDialog, scopes],
  );

  // Create/Update Permission
  const handleCreatePermission = useCallback(
    async (permissionData) => {
      try {
        const payload = {
          role_id: parseInt(permissionData.role_id),
          resource_id: parseInt(permissionData.resource_id),
          action_id: parseInt(permissionData.action_id),
          scope_id: permissionData.scope_id
            ? parseInt(permissionData.scope_id)
            : null,
        };

        if (permissionDialog.mode === "create") {
          // Create new permission
          const response = await rbacApi.post(
            "/role_permissions/role-permissions",
            payload,
          );
          setPermissions([...permissions, response.data]);
        } else {
          // Update existing permission using the permission ID
          const permissionId = permissionDialog.data.id;
          const response = await rbacApi.put(
            `/role_permissions/role-permissions/${permissionId}`,
            payload,
          );
          setPermissions(
            permissions.map((p) => (p.id === permissionId ? response.data : p)),
          );
        }

        setPermissionDialog({ open: false, mode: "create", data: null });
        setInitialPermissionData({
          role_id: "",
          resource_id: "",
          action_id: "",
          scope_id: "",
        });
        setError(null);
      } catch (err) {
        throw new Error(getUserSaveError(err));
      }
    },
    [permissionDialog, permissions],
  );

  const handleDeleteConfirm = useCallback(async () => {
    try {
      if (deleteConfirmDialog.type === "user") {
        await rbacApi.delete(`/user_crud/users/${deleteConfirmDialog.id}`);
        setUsers(
          users.filter((user) => user.userId !== deleteConfirmDialog.id),
        );
      } else if (deleteConfirmDialog.type === "role") {
        await rbacApi.delete(`/user_roles/roles/${deleteConfirmDialog.id}`);
        setRoles(roles.filter((role) => role.id !== deleteConfirmDialog.id));
      } else if (deleteConfirmDialog.type === "resource") {
        await rbacApi.delete(
          `/user_resources/resources/${deleteConfirmDialog.id}`,
        );
        setResources(
          resources.filter(
            (resource) => resource.id !== deleteConfirmDialog.id,
          ),
        );
      } else if (deleteConfirmDialog.type === "action") {
        await rbacApi.delete(`/user_actions/actions/${deleteConfirmDialog.id}`);
        setActions(
          actions.filter((action) => action.id !== deleteConfirmDialog.id),
        );
      } else if (deleteConfirmDialog.type === "scope") {
        await rbacApi.delete(`/user_scopes/scopes/${deleteConfirmDialog.id}`);
        setScopes(
          scopes.filter((scope) => scope.id !== deleteConfirmDialog.id),
        );
      } else if (deleteConfirmDialog.type === "permission") {
        const perm = deleteConfirmDialog.permissionData;
        await rbacApi.delete("/role_permissions/delete_role", {
          params: {
            role_id: perm.role_id,
            resource_id: perm.resource_id,
            action_id: perm.action_id,
            scope_id: perm.scope_id || null,
          },
        });
        setPermissions(
          permissions.filter((p, i) => i !== deleteConfirmDialog.id),
        );
      }
      setError(null);
    } catch (err) {
      setError(`Failed to delete item: ${err.message}`);
    }
    setDeleteConfirmDialog({
      open: false,
      type: "user",
      id: null,
      name: "",
      permissionData: null,
    });
  }, [
    deleteConfirmDialog.id,
    deleteConfirmDialog.type,
    deleteConfirmDialog.permissionData,
    users,
    roles,
    resources,
    actions,
    scopes,
    permissions,
  ]);

  // Create Role
  const handleCreateRole = useCallback(
    async (formData) => {
      try {
        const payload = {
          name: formData.name,
          description: formData.description,
          isDefault: formData.isDefault,
          isSystemRole: formData.isSystemRole,
          isActive: formData.isActive,
        };

        if (roleDialog.mode === "create") {
          const response = await rbacApi.post("/user_roles/roles", payload);
          setRoles([...roles, response.data]);
        } else {
          const response = await rbacApi.put(
            `/user_roles/roles/${roleDialog.data.id}`,
            payload,
          );
          setRoles(
            roles.map((r) => (r.id === roleDialog.data.id ? response.data : r)),
          );
        }

        setRoleDialog({ open: false, mode: "create", data: null });
        setError(null);
      } catch (err) {
        throw new Error(getUserSaveError(err));
      }
    },
    [roleDialog, roles],
  );

  const handleDelete = (apiPath, setter, list, id) => {
    rbacApi
      .delete(`${apiPath}/${id}`)
      .then(() => setter(list.filter((item) => item.id !== id)))
      .catch((err) => setError(`Failed to delete item: ${err.message}`));
  };

  // Filter data
  const getFilteredData = (data, keys) => {
    if (!searchTerm) return data;
    return data.filter((item) =>
      keys.some((key) =>
        String(item[key]).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  };

  // Components

  // Feature cards
  const FeatureCard = ({ title, description, icon, color, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 3,
        bgcolor: theme.palette.mode === "dark" ? "var(--bg-card)" : "#fff",
        border: `1px solid ${theme.palette.mode === "dark" ? "var(--border-color)" : "#e0e0e0"}`,
        borderLeft: `5px solid ${color}`,
        borderRadius: 2,
        cursor: "pointer",
        transition: "all 0.3s ease",
        height: "100%",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: 4,
          borderColor: color,
          borderLeftWidth: "8px",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Box sx={{ color: color, mr: 1, display: "flex" }}>{icon}</Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );

  // Stats Card

  const StatCard = ({ title, count, icon }) => (
    <Paper elevation={0} className="user-management-stat">
      <Box className="user-management-stat-label">{icon}<Typography>{title}</Typography></Box>
      <Typography className="user-management-stat-count">{count}</Typography>
    </Paper>
  );

  // Header Section
  const renderDashboardHeader = () => (
    <header className="user-management-heading">
      <Typography variant="h4">User Management</Typography>
      <Typography>This system allows you to control who can access what in your Viriqo Hires platform, configure roles, permissions, and users to secure your recruitment process.</Typography>
    </header>
  );

  // Welcome Banner
  const renderWelcomeBanner = () => (
    <Box
      sx={{
        mb: 4,
        width: "100%",
        px: 0, // REMOVE SIDE PADDING
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          width: "100%",
          bgcolor:
            theme.palette.mode === "dark"
              ? "var(--bg-card)"
              : "var(--primary-light)",
          border: "1px solid var(--border-color)",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          display: "flex",
          alignItems: "flex-start",
          boxSizing: "border-box",
        }}
      >
        <InfoIcon
          sx={{ color: "var(--primary)", mr: 2, fontSize: 32, mt: 0.5 }}
        />

        <Box sx={{ textAlign: "left" }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "var(--text-primary)", mb: 0.5 }}
          >
            Welcome to RBAC Management!
          </Typography>

          <Typography variant="body1" color="var(--text-secondary)">
            This system allows you to control who can access what in your
            Viriqo Hires platform. Configure roles, permissions, and users to
            secure your recruitment process.{" "}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );

  // Feature Card
  const renderFeatureGrid = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={4}>
        <FeatureCard
          title="Users"
          description="Manage user accounts and assign roles. Each user can have multiple roles for flexible access control."
          icon={<PeopleIcon />}
          color="#009688"
          onClick={() => {
            setActiveTab(0);
            scrollToTable();
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FeatureCard
          title="Roles"
          description="Define roles like Admin, Manager, or Viewer. Roles group permissions for easier management."
          icon={<SecurityIcon />}
          color="#2196f3"
          onClick={() => {
            setActiveTab(1);
            scrollToTable();
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FeatureCard
          title="Permissions"
          description="Grant specific permissions to roles. Control what actions each role can perform on resources."
          icon={<KeyIcon />}
          color="#4caf50"
          onClick={() => {
            setActiveTab(2);
            scrollToTable();
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FeatureCard
          title="Resources"
          description="Define what needs protection: pages, features, UI elements, or data records."
          icon={<ResourceIcon />}
          color="#9c27b0"
          onClick={() => {
            setActiveTab(3);
            scrollToTable();
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FeatureCard
          title="Actions"
          description="Define available operations: create, read, update, delete, or custom actions."
          icon={<ActionIcon />}
          color="#ff9800"
          onClick={() => {
            setActiveTab(4);
            scrollToTable();
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FeatureCard
          title="Scopes"
          description="Control the breadth of access: self (own data), team (team data), or all (system-wide)."
          icon={<ScopeIcon />}
          color="#e91e63"
          onClick={() => {
            setActiveTab(5);
            scrollToTable();
          }}
        />
      </Grid>
    </Grid>
  );

  // Stats
  const renderStats = () => (
    <Box className="user-management-stats">
      <StatCard title="Total Users" count={users.length} icon={<UserManagementIcon name="user" />} />
      <StatCard title="Active Roles" count={roles.length} icon={<UserManagementIcon name="role" />} />
      <StatCard title="Resources" count={resources.length} icon={<UserManagementIcon name="resource" />} />
      <StatCard title="Scopes" count={scopes.length} icon={<UserManagementIcon name="scope" />} />
      <StatCard title="Permissions" count={permissions.length} icon={<UserManagementIcon name="permission" />} />
      <StatCard title="Actions" count={actions.length} icon={<UserManagementIcon name="action" />} />
    </Box>
  );

  const renderDataTable = () => {
    const tabs = [
      { label: "Users", icon: <UserManagementIcon name="user" /> },
      { label: "Roles", icon: <UserManagementIcon name="role" /> },
      { label: "Permissions", icon: <UserManagementIcon name="permission" /> },
      { label: "Resources", icon: <UserManagementIcon name="resource" /> },
      { label: "Actions", icon: <UserManagementIcon name="action" /> },
      { label: "Scopes", icon: <UserManagementIcon name="scope" /> },
    ];

    // Helpers for per-tab search and filtering
    const activeSearch = (searchTerms[tabKeys[activeTab]] || "").toLowerCase();

    const getUserSearchText = (user) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      const roleName = roles.find((r) => r.id === user.roleId)?.name || "";
      const managerName = managers.find((m) => m.userId === user.manager_id)?.fullName || "";
      const scopeName = scopes.find((s) => s.id === user.scope_id)?.name || "";
      const status = user.isActive ? "active" : "inactive";
      return [
        user.firstName,
        user.lastName,
        fullName,
        user.email,
        roleName,
        managerName,
        scopeName,
        status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    };

    const getRoleSearchText = (r) => {
      return [
        r.name,
        r.description,
        r.isDefault ? "default" : "",
        r.isSystemRole ? "system" : "",
        r.isActive ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    };

    const getPermissionSearchText = (perm) => {
      const roleName = roles.find((r) => r.id === perm.role_id)?.name || "";
      const resourceName = resources.find((r) => r.id === perm.resource_id)?.displayName || resources.find((r) => r.id === perm.resource_id)?.resourceKey || "";
      const actionName = actions.find((a) => a.id === perm.action_id)?.name || "";
      const scopeName = scopes.find((s) => s.id === perm.scope_id)?.name || "all";
      return [roleName, resourceName, actionName, scopeName].filter(Boolean).join(" ").toLowerCase();
    };

    const getResourceSearchText = (res) => {
      return [res.resourceType, res.displayName, res.resourceKey, res.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    };

    const getActionSearchText = (a) => [a.name, a.description].filter(Boolean).join(" ").toLowerCase();

    const getScopeSearchText = (s) => [s.name, s.description].filter(Boolean).join(" ").toLowerCase();

    // Options for permission filters
    const roleOptions = [{ id: null, label: "All" }, ...roles.map((r) => ({ id: r.id, label: r.name }))];
    const resourceOptions = [
      { id: null, label: "All" },
      ...resources.map((r) => ({ id: r.id, label: r.displayName || r.resourceKey })),
    ];
    const actionOptions = [{ id: null, label: "All" }, ...actions.map((a) => ({ id: a.id, label: a.name }))];
    const scopeOptions = [{ id: null, label: "All" }, ...scopes.map((s) => ({ id: s.id, label: s.name }))];

    // Filtered arrays per tab
    const filteredUsers = users.filter((u) => getUserSearchText(u).includes(searchTerms.users.toLowerCase()));
    const filteredRoles = roles.filter((r) => getRoleSearchText(r).includes(searchTerms.roles.toLowerCase()));
    const filteredPermissions = permissions.filter((p) => {
      // Apply search
      const matchesSearch = getPermissionSearchText(p).includes(searchTerms.permissions.toLowerCase());
      if (!matchesSearch) return false;
      // Apply filters (if set and not explicitly 'All')
      if (permRoleFilter && permRoleFilter.id != null && Number(permRoleFilter.id) !== Number(p.role_id)) return false;
      if (permResourceFilter && permResourceFilter.id != null && Number(permResourceFilter.id) !== Number(p.resource_id)) return false;
      if (permActionFilter && permActionFilter.id != null && Number(permActionFilter.id) !== Number(p.action_id)) return false;
      if (permScopeFilter && permScopeFilter.id != null && Number(permScopeFilter.id) !== Number(p.scope_id)) return false;
      return true;
    });
    const filteredResources = resources.filter((r) => getResourceSearchText(r).includes(searchTerms.resources.toLowerCase()));
    const filteredActions = actions.filter((a) => getActionSearchText(a).includes(searchTerms.actions.toLowerCase()));
    const filteredScopes = scopes.filter((s) => getScopeSearchText(s).includes(searchTerms.scopes.toLowerCase()));

    const resetPermissionFilters = () => {
      setPermRoleFilter(null);
      setPermResourceFilter(null);
      setPermActionFilter(null);
      setPermScopeFilter(null);
      setSearchTerms((prev) => ({ ...prev, permissions: "" }));
    };

    const renderTableContent = () => {
      switch (activeTab) {
        // Users Section
        case 0:
          return (
            <Table>
              <TableHead
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "var(--bg-card)"
                      : "var(--primary-light)",
                }}
              >
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Manager</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Status</TableCell> {/* ✅ ADD */}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map(
                  (user) => {
                    const fullName = `${user.firstName} ${user.lastName}`;
                    return (
                      <TableRow key={user.userId} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {fullName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Box className="user-management-role-control" sx={{ width: 180 }}>
                            <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                              fullWidth
                              IconComponent={DropdownArrowIcon}
                              value={user.roleId || ""}
                              onChange={(e) =>
                                handleUpdateUserRole(
                                  user.userId,
                                  e.target.value || null,
                                )
                              }
                              size="small"
                              displayEmpty
                              sx={{
                                height: 40,
                                borderRadius: "12px",
                                backgroundColor: "var(--bg-card)",
                                border: "1px solid var(--border-color)",
                                fontWeight: 600,

                                "& .MuiSelect-select": {
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between", // 👈 keeps spacing consistent
                                  padding: "6px 10px",
                                  width: "100%",
                                  boxSizing: "border-box",
                                },

                                "&:hover": {
                                  borderColor: "var(--primary)",
                                },
                              }}
                              renderValue={(value) => {
                                const selectedRole = roles.find(
                                  (r) => r.id === value,
                                );

                                return <span>{selectedRole?.name || "No Role"}</span>;

                              }}
                            >
                              <MenuItem value="">No Role</MenuItem>

                              {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <UserManagementIcon name="admin" />
                                    <span>{role.name}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </TableCell>

                        <TableCell>
                          {(() => {
                            const roleName = roles.find(r => r.id === user.roleId)?.name?.toString().toLowerCase();
                            // Only Super Admin should have no manager
                            if (roleName === 'super admin' || roleName === 'superadmin') return '-';

                            return (
                              <Box className="user-management-manager-control" sx={{ width: 180 }}>
                                <Select
                    MenuProps={{ PaperProps: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
                                  fullWidth
                                  IconComponent={DropdownArrowIcon}
                                  value={user.manager_id || ''}
                                  onChange={(e) => handleUpdateUserManager(user.userId, e.target.value || null)}
                                  size="small"
                                  displayEmpty
                                  sx={{
                                    height: 40,
                                    borderRadius: "12px",
                                    backgroundColor: "var(--bg-card)",
                                    border: "1px solid var(--border-color)",
                                    fontWeight: 600,
                                    "&:hover": {
                                      borderColor: "var(--primary)",
                                    },
                                  }}
                                  renderValue={(value) => {
                                    if (!value) return "Not Assigned";
                                    const selectedManager = managers.find(m => m.userId === value);
                                    return selectedManager?.fullName || "Not Assigned";
                                  }}
                                >
                                  <MenuItem value="">Not Assigned</MenuItem>
                                  {managers.map((manager) => (
                                    <MenuItem key={manager.userId} value={manager.userId}>
                                      {manager.fullName}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Box>
                            );
                          })()}
                        </TableCell>

                        <TableCell>
                          {(() => {
                            if (!user.scope_id) return 'Use Role Scope';
                            const scope = scopes.find(s => s.id === user.scope_id);
                            return scope ? scope.name : 'Unknown';
                          })()}
                        </TableCell>

                        <TableCell>
                          <Select
                            className={user.isActive ? "user-management-status-select is-active" : "user-management-status-select is-inactive"}
                            value={Boolean(user.isActive)}
                            disabled={updatingUserId !== null}
                            onChange={event => handleUpdateUserStatus(user.userId, event.target.value)}
                            IconComponent={DropdownArrowIcon}
                            inputProps={{"aria-label": `Status for ${fullName}`}}
                            MenuProps={{PaperProps:{className:`user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}}}
                          >
                            <MenuItem value={true}><span className="user-management-status-dot is-active"/>Active</MenuItem>
                            <MenuItem value={false}><span className="user-management-status-dot is-inactive"/>Inactive</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit user">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setUserDialog({
                                  open: true,
                                  mode: "edit",
                                  data: user,
                                });
                              }}
                            >
                              <UserManagementIcon name="edit" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete user">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteConfirmDialog({
                                  open: true,
                                  type: "user",
                                  id: user.userId,
                                  name: fullName,
                                });
                              }}
                            >
                              <UserManagementIcon name="delete" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );

        // Roles Section
        case 1:
          return (
            <Table>
              <TableHead
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "var(--bg-card)"
                      : "var(--primary-light)",
                }}
              >
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>System Role</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{role.name}</TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={role.isDefault ? "Yes" : "No"}
                        size="small"
                        color={role.isDefault ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.isSystemRole ? "Yes" : "No"}
                        size="small"
                        color={role.isSystemRole ? "error" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={role.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit role">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setRoleDialog({
                              open: true,
                              mode: "edit",
                              data: role,
                            });
                          }}
                        >
                          <UserManagementIcon name="edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete role">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteConfirmDialog({
                              open: true,
                              type: "role",
                              id: role.id,
                              name: role.name,
                            });
                          }}
                        >
                          <UserManagementIcon name="delete" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRoles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No roles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );
        // Permissions Section
        case 2:
          const getPermissionDisplayNames = (perm) => {
            const roleName =
              roles.find((r) => r.id === perm.role_id)?.name || "Unknown";
            const resourceName =
              resources.find((r) => r.id === perm.resource_id)?.displayName ||
              resources.find((r) => r.id === perm.resource_id)?.resourceKey ||
              "Unknown";
            const actionName =
              actions.find((a) => a.id === perm.action_id)?.name || "Unknown";
            const scopeName =
              scopes.find((s) => s.id === perm.scope_id)?.name || "All";
            return { roleName, resourceName, actionName, scopeName };
          };

          return (
            <Table>
              <TableHead
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "var(--bg-card)"
                      : "var(--primary-light)",
                }}
              >
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPermissions.map((perm, idx) => {
                  const { roleName, resourceName, actionName, scopeName } =
                    getPermissionDisplayNames(perm);
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Chip
                          label={roleName}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{resourceName}</TableCell>
                      <TableCell>{actionName}</TableCell>
                      <TableCell>{scopeName}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit permission">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setInitialPermissionData({
                                role_id: perm.role_id,
                                resource_id: perm.resource_id,
                                action_id: perm.action_id,
                                scope_id: perm.scope_id || "",
                              });
                              setPermissionDialog({
                                open: true,
                                mode: "edit",
                                data: perm,
                              });
                            }}
                          >
                            <UserManagementIcon name="edit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete permission">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const {
                                roleName,
                                resourceName,
                                actionName,
                                scopeName,
                              } = getPermissionDisplayNames(perm);
                              setDeleteConfirmDialog({
                                open: true,
                                type: "permission",
                                id: idx,
                                name: `${roleName} > ${resourceName} > ${actionName}`,
                                permissionData: perm,
                              });
                            }}
                          >
                            <UserManagementIcon name="delete" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredPermissions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No permissions configured
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );
        // Resources Sections
        case 3: // Resources
          return (
            <Table>
              <TableHead
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "var(--bg-card)"
                      : "var(--primary-light)",
                }}
              >
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredResources.map((resource) => (
                  <TableRow key={resource.id} hover>
                    <TableCell>{resource.resourceType}</TableCell>
                    <TableCell>
                      {resource.displayName || resource.resourceKey}
                    </TableCell>
                    <TableCell>{resource.description}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit resource">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setResourceDialog({
                              open: true,
                              mode: "edit",
                              data: resource,
                            });
                          }}
                        >
                          <UserManagementIcon name="edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete resource">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteConfirmDialog({
                              open: true,
                              type: "resource",
                              id: resource.id,
                              name:
                                resource.displayName || resource.resourceKey,
                            });
                          }}
                        >
                          <UserManagementIcon name="delete" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredResources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No resources found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );

        // Actions Secxtions
        case 4:
          return (
            <Table>
              <TableHead
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "var(--bg-card)"
                      : "var(--primary-light)",
                }}
              >
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>System Action</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredActions.map((action) => (
                  <TableRow key={action.id} hover>
                    <TableCell>{action.id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {action.name}
                    </TableCell>
                    <TableCell>{action.description || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={action.isSystemAction ? "Yes" : "No"}
                        size="small"
                        color={action.isSystemAction ? "error" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit action">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setActionDialog({
                              open: true,
                              mode: "edit",
                              data: action,
                            });
                          }}
                        >
                          <UserManagementIcon name="edit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete action">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteConfirmDialog({
                              open: true,
                              type: "action",
                              id: action.id,
                              name: action.name,
                            });
                          }}
                        >
                          <UserManagementIcon name="delete" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredActions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No actions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );

        // Scopes Sections
        case 5:
          return (
            <Table>
              <TableHead
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "var(--bg-card)"
                      : "var(--primary-light)",
                }}
              >
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredScopes.map(
                  (scope) => (
                    <TableRow key={scope.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {scope.name}
                      </TableCell>
                      <TableCell>{scope.description || "-"}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit scope">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setScopeDialog({
                                open: true,
                                mode: "edit",
                                data: scope,
                              });
                            }}
                          >
                            <UserManagementIcon name="edit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete scope">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeleteConfirmDialog({
                                open: true,
                                type: "scope",
                                id: scope.id,
                                name: scope.name,
                              });
                            }}
                          >
                            <UserManagementIcon name="delete" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ),
                )}
                {filteredScopes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No scopes found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );
        default:
          return (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                Select a tab to view data.
              </Typography>
            </Box>
          );
      }
    };

    const getAddAction = () => {
      if (activeTab === 0)
        return {
          label: "Add User",
          handler: () => {
            setUserDialog({ open: true, mode: "create", data: null });
          },
        };
      if (activeTab === 1)
        return {
          label: "Add Role",
          handler: () => {
            setRoleDialog({ open: true, mode: "create", data: null });
          },
        };
      if (activeTab === 2)
        return {
          label: "Add Permission",
          handler: () => {
            setInitialPermissionData({
              role_id: "",
              resource_id: "",
              action_id: "",
              scope_id: "",
            });
            setPermissionDialog({ open: true, mode: "create", data: null });
          },
        };
      if (activeTab === 3)
        return {
          label: "Add Resource",
          handler: () => {
            setResourceDialog({ open: true, mode: "create", data: null });
          },
        };
      if (activeTab === 4)
        return {
          label: "Add Action",
          handler: () => {
            setActionDialog({ open: true, mode: "create", data: null });
          },
        };
      if (activeTab === 5)
        return {
          label: "Add Scope",
          handler: () => {
            setScopeDialog({ open: true, mode: "create", data: null });
          },
        };
      return null;
    };

    const addAction = getAddAction();

    return (
      <Paper
        className="user-management-data-card"
        elevation={0}
        sx={{
          bgcolor: theme.palette.mode === "dark" ? "var(--bg-card)" : "#fff",
          border: `1px solid ${theme.palette.mode === "dark" ? "var(--border-color)" : "#e0e0e0"}`,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        {/* Tabs */}
        <Box className="user-management-sticky-tabs"
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "var(--border-color)" : "#e0e0e0"}`,
            bgcolor: theme.palette.mode === "dark" ? "var(--bg-card)" : "#fff",
            position: "relative",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: 48,
                px: 2,
                color: "text.secondary",
              },
              "& .Mui-selected": { color: theme.palette.primary.main },
              "& .MuiTabs-indicator": {
                backgroundColor: theme.palette.primary.main,
                height: 3,
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        </Box>

        {/* Action Bar */}
        <Box className="user-management-action-bar"
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "#fafafa",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerms[tabKeys?.[activeTab] || 'users']}
            onChange={(e) =>
              setSearchTerms((prev) => ({
                ...prev,
                [tabKeys[activeTab]]: e.target.value,
              }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "white",
              width: 300,
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
          {addAction && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addAction.handler}
              sx={{
                bgcolor: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                  transform: "translateY(-2px)",
                  boxShadow: 3,
                },
                px: 3,
                py: 1,
                fontWeight: 600,
              }}
            >
              {addAction.label}
            </Button>
          )}
        </Box>

        {/* Table Area */}
        {activeTab === 2 && (
          <Box sx={{ p: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Autocomplete
                    componentsProps={{ popper: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
              size="small"
              sx={{ minWidth: 200 }}
              options={roleOptions}
              value={permRoleFilter}
              onChange={(_, v) => setPermRoleFilter(v)}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => <TextField {...params} label="Role" />}
            />
            <Autocomplete
                    componentsProps={{ popper: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
              size="small"
              sx={{ minWidth: 240 }}
              options={resourceOptions}
              value={permResourceFilter}
              onChange={(_, v) => setPermResourceFilter(v)}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => <TextField {...params} label="Resource" />}
            />
            <Autocomplete
                    componentsProps={{ popper: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
              size="small"
              sx={{ minWidth: 200 }}
              options={actionOptions}
              value={permActionFilter}
              onChange={(_, v) => setPermActionFilter(v)}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => <TextField {...params} label="Action" />}
            />
            <Autocomplete
                    componentsProps={{ popper: { className: `user-management-menu ${theme.palette.mode === "dark" ? "user-management-dark" : ""}` } }}
              size="small"
              sx={{ minWidth: 160 }}
              options={scopeOptions}
              value={permScopeFilter}
              onChange={(_, v) => setPermScopeFilter(v)}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => <TextField {...params} label="Scope" />}
            />
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={filteredPermissions.length}
                size="small"
                sx={{ fontWeight: 700, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}
              />
              <Button variant="outlined" onClick={resetPermissionFilters}>
                Reset Filters
              </Button>
            </Box>
          </Box>
        )}

        <TableContainer className="user-management-table-container">
          {renderTableContent()}
        </TableContainer>
      </Paper>
    );
  };

  // Dialog Components
  const StyledDialog = React.memo(
    ({ open, onClose, title, children, onSave, saveLabel = "Save" }) => (
      <Dialog
        className={`user-management-dialog ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            pb: 1,
          }}
        >
          {title}
          <IconButton
            onClick={onClose}
            aria-label="Close dialog"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 1 }}>{children}</Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Button onClick={onClose} color="inherit">
            <UserManagementIcon name="cancel" /> Cancel
          </Button>
          <Button
            onClick={onSave}
            variant="contained"
            sx={{
              bgcolor: theme.palette.primary.main,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          >
            {saveLabel}
          </Button>
        </DialogActions>
      </Dialog>
    ),
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor:
            theme.palette.mode === "dark" ? "background.default" : "#f8f9fa",
        }}
      >
        <CircularProgress
          size={60}
          sx={{ color: theme.palette.primary.main }}
        />
      </Box>
    );
  }

  // Check if user has permission to manage users
  if (!canManageUsers) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor:
            theme.palette.mode === "dark" ? "background.default" : "#f8f9fa",
          p: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: theme.palette.mode === "dark" ? "var(--bg-card)" : "#fff",
            border: `1px solid ${theme.palette.mode === "dark" ? "var(--border-color)" : "#e0e0e0"}`,
            borderRadius: 2,
            maxWidth: 500,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
          >
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You don't have access to User Management. Contact your administrator
            if you believe this is a mistake.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      className={`user-management-page ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}
      sx={{
        p: 4,
        bgcolor:
          theme.palette.mode === "dark" ? "background.default" : "#f8f9fa",
        minHeight: "100vh",
        backgroundImage:
          theme.palette.mode === "dark"
            ? "none"
            : `linear-gradient(135deg, ${theme.palette.primary.light}10 0%, ${theme.palette.secondary.light}10 100%)`,
        pb: 8,
      }}
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {renderDashboardHeader()}
      {renderStats()}
      <div className="user-management-scroll-content">
        <Box className="user-management-table-region" ref={tableRef}>{renderDataTable()}</Box>
      </div>
      {/* Dialogs */}
      <UserFormDialog
        open={userDialog.open}
        onClose={() => setUserDialog({ ...userDialog, open: false })}
        onSave={handleCreateUser}
        mode={userDialog.mode}
        initialData={userDialog.data}
        roles={roles}
        managers={managers}
        scopes={scopes}
      />


      <RoleFormDialog
        open={roleDialog.open}
        onClose={() => setRoleDialog({ ...roleDialog, open: false })}
        onSave={handleCreateRole}
        mode={roleDialog.mode}
        initialData={roleDialog.data}
      />

      <PermissionFormDialog
        open={permissionDialog.open}
        onClose={() => {
          setPermissionDialog({ open: false, mode: "create", data: null });
          setInitialPermissionData({
            role_id: "",
            resource_id: "",
            action_id: "",
            scope_id: "",
          });
        }}
        onSave={handleCreatePermission}
        mode={permissionDialog.mode}
        roles={roles}
        resources={resources}
        actions={actions}
        scopes={scopes}
        initialPermission={initialPermissionData}
        theme={theme}
      />

      <ResourceFormDialog
        open={resourceDialog.open}
        onClose={() => setResourceDialog({ ...resourceDialog, open: false })}
        onSave={handleCreateResource}
        mode={resourceDialog.mode}
        initialData={resourceDialog.data}
      />

      <ActionFormDialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ ...actionDialog, open: false })}
        onSave={handleCreateAction}
        mode={actionDialog.mode}
        initialData={actionDialog.data}
      />

      <ScopeFormDialog
        open={scopeDialog.open}
        onClose={() => setScopeDialog({ ...scopeDialog, open: false })}
        onSave={handleCreateScope}
        mode={scopeDialog.mode}
        initialData={scopeDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmDialog.open}
        onClose={() =>
          setDeleteConfirmDialog({
            open: false,
            type: "user",
            id: null,
            name: "",
            permissionData: null,
          })
        }
        onConfirm={handleDeleteConfirm}
        title={`Delete ${deleteConfirmDialog.type.charAt(0).toUpperCase() + deleteConfirmDialog.type.slice(1)}`}
        message={`Are you sure you want to delete "${deleteConfirmDialog.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />
    </Box>
  );
};

export default UserManagement;
